import Border from '@ui/effects/Border'
import ButtonsFixed, { SpaceButtons } from '@ui/buttons/ButtonsFixed'
import PressableIcon from '@ui/buttons/PressableIcon'
import { colors } from '@ui/colors'
import EmptyResult from '@ui/components/EmptyResult'
import Page from '@shell/Page'
import Header from '@ui/elements/Header'
import Options from '@ui/elements/OptionBadge/Options'
import { OptionBadgeProps } from '@ui/elements/OptionBadge/types'
import LinkedOrdersModal from '@order/modals/Checkout/LinkedOrders/LinkedOrdersModal'
import { LinkedOrdersOption } from '@order/modals/Checkout/LinkedOrders/types'
import SummaryModal, { Preview } from '@order/modals/Summary/SummaryModal'
import AvatarStartModal from 'features/avatar/StartModal'
import { SavedOrder } from '@order/hooks/state/orderState'
import {
  resolveProvider,
  useOrdersMethods,
  usePaymentItems,
  usePaymentItemsMethods,
} from '@order/hooks/usePaymentMethods'
import usePayments from '@order/hooks/usePayments'
import useStartOrder from 'features/order/hooks/useStartOrder'
import useAvatarFlow from 'features/avatar/useAvatarFlow'
import useRestartOrder from '@order/hooks/useRestartOrder'
import { router } from 'expo-router'
import { useIsFocused } from '@ui/hooks/useIsFocused'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'

import CartUtils from '@order/CartUtils'
import OrderCheckbox from './OrderCheckbox/OrderCheckbox'
import { borderRadius } from './OrderCheckbox/styles'
import OrderRemove from './OrderRemove'
import useCartItems from '@order/hooks/useCart'
import { useLogout } from 'hooks/useLogout'
import Button from '@ui/buttons/Button'
import { useDiscountsEnabled } from '@order/useDiscounts'
import { useOrderScenarios } from '@order/hooks/useScenario'
import DealBanner from './DealBanner'
import useDeal from '@order/hooks/useDeal'
import { getFlow } from '@order/utils/getFlow'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useAppRegion } from 'storage/useRemoteConfigParams'
import ConfirmationModal from '@ui/components/ConfirmationModal'
import {
  PaymentMethod,
  PaymentMethodAvailability,
  PaymentUtils,
} from '@ca/common'
import PaymentMethodsInfoModal from 'features/payment/modals/PaymentMethodsInfo/PaymentMethodsInfoModal'
import { useCurrencyDecimals } from 'hooks/useCurrency'

const useDiscountsButton = ({
  savedOrderId,
  setPreview,
}: {
  savedOrderId?: string
  setPreview: (preview: Preview | null) => void
}) => {
  const scenarios = useOrderScenarios(savedOrderId ?? '')
  // FIXME: We should fix this check to use all scenarios
  const discountsEnabled = useDiscountsEnabled(scenarios[0])

  const goToDiscounts = useCallback(() => {
    if (!savedOrderId) return
    router.navigate(`/order/discounts/${savedOrderId}`)
    setPreview(null)
  }, [savedOrderId, setPreview])

  return {
    onPress: discountsEnabled ? goToDiscounts : undefined,
  }
}

const getOrdersTotal = (savedOrders: SavedOrder[], decimals: number) => {
  const orders = savedOrders.flatMap((o) => o.result?.orders ?? [])
  const discounts = savedOrders.flatMap((o) => o.discounts)
  if (orders.length === 0) return 0
  return PaymentUtils.getPaymentTotal(orders, discounts, decimals)
}

export default function Checkout() {
  const { t } = useTranslation(['ui', 'order'])
  const text = `order:checkout`

  // const avatarSession = useAvatarSessionEnd()

  // useEffect(() => {
  //   // End any existing session
  //   avatarSession.end()
  // }, [])

  const { list: orders, payments, dictionary } = useCartItems()
  const logout = useLogout({ navigate: true })
  const isOrderSingle = orders.length === 1
  const region = useAppRegion()
  const isCombined = region.linkedOrders?.behavior === 'combined'

  const { restart: addMore } = useRestartOrder()
  const createPayment = usePayments((s) => s.createPayment)

  const [orderIds, setOrderIds] = useState<string[]>([])
  const [preview, setPreview] = useState<Preview | null>(null)
  const [linkedOrdersModal, setLinkedOrdersModal] = useState<string | null>(
    null
  )
  const [isStartModalOpen, setStartModalOpen] = useState(false)
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false)
  const [isPaymentMethodsModalOpen, setPaymentMethodsModalOpen] =
    useState(false)

  const isFocused = useIsFocused()

  const selectedOrders = useMemo(
    () => orders.filter((o) => orderIds.includes(o.id)),
    [orders, orderIds]
  )
  const paymentItems = usePaymentItems(selectedOrders)
  // const allPaymentItems = usePaymentItems(orders)

  const { methods, availability: paymentMethodsAvailability } =
    usePaymentItemsMethods(paymentItems)

  const decimals = useCurrencyDecimals()
  const toPay = useMemo(
    () => getOrdersTotal(selectedOrders, decimals),
    [selectedOrders, decimals]
  )

  const handlePay = () => {
    const method = methods[0] ?? 'terminal'
    const id = createPayment({
      method: method,
      orderIds,
      amount: toPay,
      provider: resolveProvider(method),
    })
    router.navigate(`/payment/settings/${id}`)
  }

  const hasOrderPayment = useCallback(
    (orderId: string) => {
      const payment = payments.get(orderId)
      return !!payment
    },
    [payments]
  )

  const addOrder = (orderId: string) => {
    const isAdded = orderIds.includes(orderId)
    if (isAdded) return

    const order = dictionary[orderId]
    // Should never happen
    if (!order) return

    const primary = CartUtils.getPrimary(order, dictionary)

    // Should not happen
    // in case we have a payment for the primary order that is not considered finished
    if (
      primary &&
      primary.payment &&
      !CartUtils.payments.isFinished(primary.payment)
    ) {
      return
    }

    if (hasOrderPayment(orderId)) return

    setOrderIds((prev) => [...prev, orderId])
  }

  const removeOrder = (orderId: string) => {
    const isRemoved = orderIds.includes(orderId)
    if (!isRemoved) return
    if (hasOrderPayment(orderId)) return
    setOrderIds((prev) => prev.filter((id) => id !== orderId))
  }

  const handleRemovedOrders = useCallback((orderIds: string[]) => {
    setOrderIds((prev) => prev.filter((id) => !orderIds.includes(id)))
  }, [])

  const toggleOrder = (orderId: string) => {
    const isSelected = orderIds.includes(orderId)
    if (isSelected) removeOrder(orderId)
    else addOrder(orderId)
  }

  const handleSelectOrder = (order: SavedOrder) => {
    const isSelected = CartUtils.isSelected(order, orderIds)

    const { isPrimary, isDependent, hasDependents, dependents } =
      CartUtils.getItemInfo(order, orders)

    const isLinked = isDependent || hasDependents

    if (!isLinked) {
      toggleOrder(order.id)
      return
    }

    if (isDependent) {
      const primary = CartUtils.getPrimary(order, orders)
      const isPrimarySelected =
        primary && CartUtils.isSelected(primary, orderIds)

      if (isCombined) {
        // Toggle the entire group as a unit
        const allGroupDependents = primary
          ? CartUtils.getDependents(primary, orders)
          : dependents
        if (isSelected) {
          allGroupDependents.forEach((d) => removeOrder(d.id))
          if (primary) removeOrder(primary.id)
        } else {
          if (primary && !isPrimarySelected) addOrder(primary.id)
          allGroupDependents.forEach((d) => addOrder(d.id))
        }
        return
      }

      if (primary && !isPrimarySelected) addOrder(primary.id)
      toggleOrder(order.id)
      return
    }

    if (isPrimary) {
      if (!isSelected) {
        addOrder(order.id)
        if (isCombined) {
          dependents.forEach((d) => addOrder(d.id))
        }
        return
      }

      if (isCombined) {
        dependents.forEach((d) => removeOrder(d.id))
        removeOrder(order.id)
        return
      }

      let isDependentSelected = false
      dependents.forEach((dependent) => {
        if (CartUtils.isSelected(dependent, orderIds)) {
          isDependentSelected = true
        }
      })
      if (isDependentSelected) {
        setLinkedOrdersModal(order.id)
        return
      }
      removeOrder(order.id)
      return
    }
  }

  const handleLinkedOrderChoice = (option: LinkedOrdersOption) => {
    if (!linkedOrdersModal) return

    const order = orders.find((o) => o.id === linkedOrdersModal)
    if (!order) return

    const primary = CartUtils.getPrimary(order, orders) || order
    const dependents = CartUtils.getDependents(primary, orders)

    if (option === 'together') {
      dependents.forEach((dependent) => addOrder(dependent.id))
      addOrder(order.id)
    }

    if (option === 'separately') {
      dependents.forEach((dependent) => removeOrder(dependent.id))
      addOrder(order.id)
    }

    setLinkedOrdersModal(null)
  }

  const isPaymentAllowed = useMemo(() => {
    return methods.length > 0
  }, [methods])

  const allPaymentMethods = useOrdersMethods(orders)

  const hasSelected = orderIds.length > 0

  // This merges the availability of the payment methods for the selected orders with the availability of the payment methods for all orders
  const allMethodsAvailability = useMemo(() => {
    const map = new Map<PaymentMethod, PaymentMethodAvailability>(
      paymentMethodsAvailability.map((m) => [m.method, m])
    )

    return allPaymentMethods.availability.map((m) => {
      const resolved = map.get(m.method) || m
      if (hasSelected) return resolved

      // If we don't have selected orders, we show all payment methods as available
      return {
        ...resolved,
        available: true,
        disableReasons: [],
      }
    })
  }, [paymentMethodsAvailability, allPaymentMethods, hasSelected])

  const optionsPayment: OptionBadgeProps[] = useMemo(() => {
    return allMethodsAvailability.map((method) => ({
      text: t(`types:payment.method.${method.method}`),
      status: hasSelected
        ? method.available
          ? 'active'
          : 'disabled'
        : 'default',
    }))
  }, [t, allMethodsAvailability, hasSelected])

  const ordersToShow = orders.filter(({ primaryId }) => !primaryId)
  const isOrdersEmpty = ordersToShow.length === 0

  const allPaid = useMemo(() => {
    return orders.every((item) => hasOrderPayment(item.id))
  }, [orders, hasOrderPayment])

  useEffect(() => {
    if (!isFocused) {
      setOrderIds([])
      return
    }

    if (isOrderSingle) {
      addOrder(orders[0].id)
    }
  }, [isFocused, isOrderSingle, orders])

  const discountsButton = useDiscountsButton({
    savedOrderId: preview?.orderId,
    setPreview,
  })

  const startOrder = useStartOrder()
  const { isAvatar } = useAvatarFlow()

  const handleAddMore = useCallback(() => {
    if (isAvatar) {
      setStartModalOpen(true)
      return
    }

    addMore()
  }, [addMore, isAvatar])

  return (
    <>
      <Page stage="order" back={false}>
        <Header
          title={t(`${text}.header.title`)}
          desc={t(`${text}.header.desc`)}
        />

        <View className="flex px-8">
          <View className="flex items-center justify-center">
            {!isOrdersEmpty && (
              <Options
                options={optionsPayment}
                onPress={() => setPaymentMethodsModalOpen(true)}
              />
            )}
          </View>
          {isOrdersEmpty && (
            <EmptyResult
              title={t(`${text}.empty.result.title`)}
              desc={t(`${text}.empty.result.desc`)}
              button={{
                variant: 'filled',
                label: t(`${text}.empty.result.cta`),
                onPress: handleAddMore,
              }}
            />
          )}
        </View>

        <View className="mt-8 flex flex-col gap-8 px-8">
          {ordersToShow
            .filter(({ primaryId }) => !primaryId)
            .map((order) => {
              const hasDependents = CartUtils.hasDependents(order, orders)

              const infoComponent =
                hasDependents && !isCombined ? (
                  <PressableIcon
                    name="info-circle"
                    onPress={() => setLinkedOrdersModal(order.id)}
                    color={colors.neutral[400]}
                  />
                ) : null

              return (
                <View
                  key={order.id}
                  className="flex flex-col gap-0 overflow-hidden"
                  style={{ borderRadius }}
                >
                  <OrderCheckbox
                    data={order}
                    value={orderIds.includes(order.id)}
                    onChange={() => handleSelectOrder(order)}
                    setPreview={setPreview}
                    infoComponent={infoComponent}
                    expanded={orders.length === 1}
                  />
                  {orders
                    .filter(({ primaryId }) => primaryId === order.id)
                    .map((dependent) => (
                      <View key={dependent.id} className="flex flex-col">
                        <OrderCheckbox
                          data={dependent}
                          value={orderIds.includes(dependent.id)}
                          onChange={() => handleSelectOrder(dependent)}
                          setPreview={setPreview}
                          infoComponent={infoComponent}
                        />
                      </View>
                    ))}
                  <Border style={[{ borderRadius }]} />
                </View>
              )
            })}
        </View>
        <DealBanner />
        <SpaceButtons />
      </Page>
      <ButtonsFixed
        buttons={[
          {
            label: t('ui:addMore'),
            iconStart: 'plus',
            variant: 'outlined',
            onPress: handleAddMore,
          },
          !allPaid
            ? {
                label: t('ui:payment'),
                iconEnd: 'arrow-narrow-right',
                variant: 'filled',
                onPress: handlePay,
                disabled: !isPaymentAllowed,
                className: 'border-none',
              }
            : {
                label: t('ui:logout'),
                variant: 'outlined',
                onPress: () => setIsLogoutModalOpen(true),
                iconEnd: 'logout',
                overrideColorFront: colors.red[600],
                className: 'border-red-600',
                colors: [colors.red0, colors.red10],
              },
        ]}
      />
      <AvatarStartModal
        open={isStartModalOpen}
        close={() => setStartModalOpen(false)}
        onStartGuidance={() => {
          setStartModalOpen(false)
          startOrder('avatar')
        }}
        onBrowseDeals={() => {
          setStartModalOpen(false)
          // Explicitly disable avatar flow
          addMore({ avatarFlow: false })
        }}
      />
      <SummaryModal
        orders={orders}
        preview={preview}
        onDiscountPress={discountsButton.onPress}
        onClose={() => setPreview(null)}
      >
        <View className="flex flex-1 flex-row items-center justify-start gap-8 px-8 pb-8">
          <OrderRemove
            onRemove={handleRemovedOrders}
            preview={preview}
            setPreview={setPreview}
          />
          <Button
            label={t('ui:close')}
            variant="outlined"
            onPress={() => setPreview(null)}
          />
        </View>
      </SummaryModal>
      <LinkedOrdersModal
        open={!!linkedOrdersModal}
        close={() => setLinkedOrdersModal(null)}
        onSelect={handleLinkedOrderChoice}
      />
      <ConfirmationModal
        open={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={logout}
        title={t('modals:logout.customer.title')}
        desc={t('modals:logout.customer.desc')}
      />
      <PaymentMethodsInfoModal
        open={isPaymentMethodsModalOpen}
        close={() => setPaymentMethodsModalOpen(false)}
        hasSelection={hasSelected}
        availability={allMethodsAvailability}
      />
    </>
  )
}
